// Generated by TaroBase SDK Generator: 2026-05-01T17:36:41.366Z
// Collection: tenants/$appid
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Multi-tenant configuration for Poofy Pay merchants. Service (PROJECT_VAULT_ADDRESS) creates tenants during onboarding/seeding; tenant owner reads/updates their own record.
 */

export interface TenantsRequest {
  appid: string;
  owner: AddressType;
  name: string;
  defaultRecipientWallet: AddressType;
  defaultAcceptedRails: string;
  backendUrl?: string;
  paymentModel?: string;
  whopProductId?: string;
  productWebhook?: string;
  createdAt: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface TenantsResponse {
  appid: string;
  owner: string;
  name: string;
  defaultRecipientWallet: string;
  defaultAcceptedRails: string;
  backendUrl?: string;
  paymentModel?: string;
  whopProductId?: string;
  productWebhook?: string;
  createdAt: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Tenants operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildTenants(appid: string, data: TenantsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `tenants/${appid}`, document: doc };
}

/**
 * Service-only (PROJECT_VAULT_ADDRESS). Backend signs the tenant registration and sets owner and createdAt; secrets are written separately to tenantSecrets/$appid during seeding/onboarding. Document ID is the tenant appid. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setTenants(appid: string, data: TenantsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`tenants/${appid}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Tenants: ${formatError(error)}`);
    return false;
  }
}

export type TenantsRequestUpdate = Partial<TenantsRequest>;

/**
 * Build a Tenants update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateTenants(appid: string, data: TenantsRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `tenants/${appid}`, document: doc };
}

/**
 * Tenant owner can update mutable fields (name, defaultRecipientWallet, defaultAcceptedRails, whopProductId, productWebhook); appid/owner/createdAt are readonly after creation. Service vault and admin can update any mutable field. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateTenants(appid: string, data: TenantsRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`tenants/${appid}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Tenants: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Tenant owner, service vault, or admin can read. Returns only safe tenant config.
   (Get Single Item)
 */
export async function getTenants(appid: string): Promise<TenantsResponse | null> {
  try {
    const result = await get(`tenants/${appid}`);
    return result as TenantsResponse | null;
  } catch (error) {
    console.error(`Error getting Tenants: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Tenants document. (
  Read Operation Details: Tenant owner, service vault, or admin can read. Returns only safe tenant config.
  )
 */
export function subscribeTenants(callback: (data: TenantsResponse | null) => void, appid: string): Promise<() => Promise<void>> {
  return subscribe(`tenants/${appid}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as TenantsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Tenants at path '${`tenants/${appid}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Tenants items from collection tenants
 
  Read Operation Details: Tenant owner, service vault, or admin can read. Returns only safe tenant config.
  
 */
export async function getManyTenants(filter: string = ""): Promise<TenantsResponse[]> {
  try {
    const results = await get("tenants", { prompt: filter });
    return Array.isArray(results) ? results as TenantsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Tenants collection at path 'tenants': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Tenants collection at tenants
 
  Read Operation Details: Tenant owner, service vault, or admin can read. Returns only safe tenant config.
  
 */
export function subscribeManyTenants(callback: (data: TenantsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("tenants", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as TenantsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Tenants collection at path '${"tenants"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Tenants items from collection tenants
 
  Read Operation Details: Tenant owner, service vault, or admin can read. Returns only safe tenant config.
  
 */
export async function getAllTenants(filter: string = ""): Promise<TenantsResponse[]> {
  try {
    const results = await get("tenants", { prompt: filter });
    return Array.isArray(results) ? results as TenantsResponse[] : [];
  } catch (error) {
    console.error(`Error getting Tenants collection at path 'tenants': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Tenants collection at tenants
 
  Read Operation Details: Tenant owner, service vault, or admin can read. Returns only safe tenant config.
  
 */
export function subscribeAllTenants(callback: (data: TenantsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("tenants", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as TenantsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Tenants collection at path '${"tenants"}': ${formatError(error)}`);
    }
  });
}
